import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Toggle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { ClearFiltersButton } from "@/components/ClearFiltersButton";
import { FiltersToggleButton } from "@/components/FiltersToggleButton";

import {
  HUB_ORDERS_GROUP_BY_OPTIONS,
  NATIVE_FULFILLMENT_OPTIONS,
  type OrdersGroupBy,
  type OrdersListQuery,
} from "../../lib/orders-list-model";
import {
  STATUS_FILTER_OPTION_ITEM_CLASS,
  StatusOptionContent,
} from "./StatusOptionContent";

interface OrdersHubToolbarProps {
  query: OrdersListQuery;
  orderCount: number;
  attentionCount: number;
  onExport?: () => void;
  exportDisabled?: boolean;
  exportPending?: boolean;
  onChange: (next: OrdersListQuery) => void;
}

const GROUP_LABEL: Record<(typeof HUB_ORDERS_GROUP_BY_OPTIONS)[number], { key: string; fallback: string }> = {
  none: { key: "marketplace.orders.list.groupNone", fallback: "No grouping" },
  status: { key: "marketplace.orders.list.groupStatus", fallback: "Status" },
  deadline: { key: "marketplace.orders.list.groupDeadline", fallback: "Due date" },
};

function hubFilterCount(query: OrdersListQuery): number {
  return (
    (query.statuses.length > 0 ? 1 : 0) +
    (query.attentionOnly ? 1 : 0) +
    (query.overdueOnly ? 1 : 0)
  );
}

export function OrdersHubToolbar({
  query,
  orderCount,
  attentionCount,
  onExport,
  exportDisabled = false,
  exportPending = false,
  onChange,
}: OrdersHubToolbarProps) {
  const { t } = useTranslation();
  const searchId = useId();
  const panelId = useId();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const activeFilterCount = hubFilterCount(query);
  const searchPlaceholder = t("personaOpportunities.hub.sent.searchPlaceholder", "Search orders");

  const toggleStatus = (status: MarketplaceFulfillmentStatus, checked: boolean) => {
    const next = checked
      ? [...query.statuses, status]
      : query.statuses.filter((current) => current !== status);
    onChange({ ...query, statuses: next });
  };

  const clearFacetFilters = () => {
    onChange({
      ...query,
      statuses: [],
      attentionOnly: false,
      overdueOnly: false,
    });
  };

  return (
    <div className="flex shrink-0 flex-col gap-3 rounded-md border border-border bg-card px-4 py-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <span className="text-[13px] font-medium text-foreground">
            {t("personaOpportunities.hub.sent.summary", {
              count: orderCount,
              defaultValue_one: "{{count}} order",
              defaultValue_other: "{{count}} orders",
            })}
          </span>
          <label htmlFor={searchId} className="sr-only">
            {searchPlaceholder}
          </label>
          <Input
            id={searchId}
            type="search"
            value={query.search}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="h-8 w-full min-w-[10rem] sm:w-[16rem] sm:flex-none"
            onChange={(event) => onChange({ ...query, search: event.target.value })}
          />
          <FiltersToggleButton
            isOpen={isFiltersOpen}
            activeCount={activeFilterCount}
            onToggle={() => setIsFiltersOpen((open) => !open)}
            panelId={panelId}
          />
          {activeFilterCount > 0 ? <ClearFiltersButton onClear={clearFacetFilters} /> : null}
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-muted-foreground">
              {t("personaOpportunities.hub.groupBy.label", "Group by")}
            </span>
            <Select
              value={query.groupBy}
              onValueChange={(value) => onChange({ ...query, groupBy: value as OrdersGroupBy })}
            >
              <SelectTrigger
                className="h-8 w-[11rem]"
                aria-label={t("personaOpportunities.hub.groupBy.label", "Group by")}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HUB_ORDERS_GROUP_BY_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {t(GROUP_LABEL[option].key, GROUP_LABEL[option].fallback)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {onExport ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              iconStart={exportPending ? undefined : "download"}
              className="h-8 text-xs"
              disabled={exportDisabled || exportPending}
              aria-busy={exportPending || undefined}
              onClick={onExport}
            >
              {exportPending ? (
                <>
                  <Icon
                    name="loader"
                    size="sm"
                    color="inherit"
                    className="animate-spin"
                    aria-hidden
                  />
                  {t("marketplace.orders.list.export.exporting", "Exporting…")}
                </>
              ) : (
                t("marketplace.orders.list.export.excel", "Export Excel")
              )}
            </Button>
          </div>
        ) : null}
      </div>
      {isFiltersOpen ? (
        <div
          id={panelId}
          className="flex flex-wrap items-center gap-2 border-t border-border pt-3 duration-150 animate-in fade-in slide-in-from-top-1"
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                iconEnd="chevron-down"
                className={cn(
                  "h-8 text-xs",
                  query.statuses.length > 0 && "border-primary/40 bg-primary-subtle/40 text-primary",
                )}
              >
                {t("marketplace.orders.list.statusFilter", "Status")}
                {query.statuses.length > 0 ? (
                  <span className="tabular-nums">{query.statuses.length}</span>
                ) : null}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-72">
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {t("marketplace.orders.list.statusFilter", "Status")}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {NATIVE_FULFILLMENT_OPTIONS.map((status) => (
                <DropdownMenuCheckboxItem
                  key={status}
                  className={STATUS_FILTER_OPTION_ITEM_CLASS}
                  checked={query.statuses.includes(status)}
                  onSelect={(event) => event.preventDefault()}
                  onCheckedChange={(checked) => toggleStatus(status, checked === true)}
                >
                  <StatusOptionContent status={status} />
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <Toggle
                variant="outline"
                size="sm"
                pressed={query.attentionOnly}
                aria-label={t("marketplace.orders.list.attentionFilter", "Needs attention")}
                className={cn(
                  "h-8 gap-1.5 px-3 text-xs",
                  query.attentionOnly &&
                    "border-error/40 bg-error-subtle/60 text-error-subtle-foreground",
                )}
                onPressedChange={(pressed) => onChange({ ...query, attentionOnly: pressed })}
              >
                <Icon name="alert-circle" size="sm" color="inherit" aria-hidden />
                {t("marketplace.orders.list.attentionFilter", "Needs attention")}
                {attentionCount > 0 ? (
                  <span className="tabular-nums text-muted-foreground">{attentionCount}</span>
                ) : null}
              </Toggle>
            </TooltipTrigger>
            <TooltipContent>
              {t(
                "marketplace.orders.list.attentionFilterHint",
                "Overdue, waiting on your approval, or failed to reach the vendor",
              )}
            </TooltipContent>
          </Tooltip>
          <Toggle
            variant="outline"
            size="sm"
            pressed={query.overdueOnly}
            aria-label={t("marketplace.orders.list.overdueOnly", "Overdue only")}
            className={cn(
              "h-8 gap-1.5 px-3 text-xs",
              query.overdueOnly && "border-error/40 bg-error-subtle/60 text-error-subtle-foreground",
            )}
            onPressedChange={(pressed) => onChange({ ...query, overdueOnly: pressed })}
          >
            <Icon name="alert-circle" size="sm" color="inherit" aria-hidden />
            {t("marketplace.orders.list.overdueOnly", "Overdue only")}
          </Toggle>
        </div>
      ) : null}
    </div>
  );
}
